import { useState } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import Layout from "../components/Layout";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  IconButton,
  Snackbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  useGenerateChatBotUrlQuery,
  useGetUserQuery,
  useDismissAiBannerMutation,
} from "features/auth/auth";
import { useAppSelector } from "features/store";
import { useViewAs } from "hooks/useViewAs";
import {
  VIEWPORT_2K_MIN_MEDIA,
  VIEWPORT_4K_MIN_MEDIA,
} from "config/layout";
import ContactSupportModal from "components/chat/ContactSupportModal";
import DarkCtaButton from "components/common/DarkCtaButton";
import CustomScrollbars from "components/CustomScrollbars";
import { CALIBRATE_FONT_FAMILY_ACCENT } from "components/hoc/Theme";

const HOW_TO_USE_STEPS = [
  {
    title: "Ask questions in plain English.",
    body: "Your agent has direct access to your payroll data and will pull real numbers.",
  },
  {
    title: "Refine your results.",
    body: "Follow up to filter, compare, or narrow by department or date range.",
  },
  {
    title: "Export your data.",
    body: "Ask the agent to generate an Excel file to download directly from the chat.",
  },
];

const EXAMPLE_PROMPTS = [
  "Show the total payroll cost for each year since 2024.",
  "Compare overtime payments between departments in Jan 2026.",
  "Show the total company costs for medical plans in 2025.",
  "Compare the total 401K deductions between 2024 and 2025.",
  "Which months had the highest overtime spend?",
];

function rtkQueryErrorMessage(error: unknown): string {
  if (!error || typeof error !== "object") {
    return "Something went wrong. Please try again.";
  }
  if ("status" in error && error.status === "FETCH_ERROR") {
    return "Network error. Check your connection and try again.";
  }
  if ("status" in error && error.status === "PARSING_ERROR") {
    return "Could not read the server response.";
  }
  if ("status" in error && error.status === "TIMEOUT_ERROR") {
    return "The request timed out. Try again.";
  }
  if (
    "status" in error &&
    error.status === "CUSTOM_ERROR" &&
    "error" in error
  ) {
    const msg = (error as { error?: unknown }).error;
    if (typeof msg === "string" && msg.trim()) return msg;
  }
  const data = (error as FetchBaseQueryError).data;
  if (data != null && typeof data === "object") {
    const d = data as Record<string, unknown>;
    const detail = d.detail ?? d.message ?? d.error;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (
      Array.isArray(detail) &&
      detail.length &&
      typeof detail[0] === "string"
    ) {
      return detail[0];
    }
  }
  return "Unable to load AI Reporting right now. Please try again.";
}

export default function ChatPage() {
  const [supportOpen, setSupportOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const webAccessDenied = useAppSelector((state) => state.auth.webAccessDenied);
  const { isClientView, viewedUserId } = useViewAs();
  const chatBotUrlKey = isClientView ? `client-${viewedUserId}` : "self";

  const {
    data: user,
    isLoading: userLoading,
    error: userError,
    refetch: refetchUser,
  } = useGetUserQuery(undefined, {
    skip: !!webAccessDenied,
  });

  const {
    data,
    error: chatUrlError,
    isLoading: chatUrlLoading,
    isFetching: chatUrlFetching,
    refetch: refetchChatUrl,
    isSuccess: chatUrlSuccess,
  } = useGenerateChatBotUrlQuery(chatBotUrlKey, {
    refetchOnMountOrArgChange: true,
  });

  const [dismissAiBanner] = useDismissAiBannerMutation();

  const chatUrlBusy = chatUrlLoading || chatUrlFetching;
  const iframeUrl =
    !chatUrlBusy && data && typeof data.url === "string" && data.url.trim()
      ? data.url.trim()
      : null;
  // The AI switch is on but nobody has set up an agent yet: nothing to talk to.
  const agentNotSetUp = !chatUrlBusy && data?.configured === false;
  const chatUrlMissing =
    !chatUrlBusy &&
    !chatUrlError &&
    !agentNotSetUp &&
    chatUrlSuccess &&
    (!data?.url || String(data.url).trim() === "");

  const showBanner = !isClientView && user?.has_seen_ai_banner === false;

  const handleDismissBanner = () => {
    void dismissAiBanner();
  };

  const handleCopyPrompt = async (prompt: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setSnackbar("Copied to clipboard");
    } catch {
      setSnackbar("Could not copy. Please copy manually.");
    }
  };

  const iframeLoader = (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <CircularProgress />
    </Box>
  );

  const infoCards = (
    <>
      <Card
        elevation={2}
        sx={{
          p: 2.5,
          backgroundColor: (t) => alpha(t.palette.calibrate.howToUseBg, 0.5),
        }}
      >
        <Typography
          sx={{
            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
            fontWeight: 700,
            letterSpacing: 0.5,
            fontSize: "1.125rem",
            mb: 2,
          }}
        >
          HOW TO USE
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {HOW_TO_USE_STEPS.map((step, i) => (
            <Box key={step.title} sx={{ display: "flex", gap: 1.5 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: "secondary.main",
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                }}
              >
                {i + 1}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.3 }}>
                  {step.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                    color: "text.secondary",
                  }}
                >
                  {step.body}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Card>

      <Card elevation={2} sx={{ p: 2.5 }}>
        <Typography
          sx={{
            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
            fontWeight: 700,
            letterSpacing: 0.5,
            fontSize: "1.125rem",
          }}
        >
          EXAMPLE PROMPTS
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
            color: "text.secondary",
            mt: 0.5,
            mb: 2,
          }}
        >
          Try an example prompt or ask your own question to get started.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {EXAMPLE_PROMPTS.map((prompt) => (
            <Box
              key={prompt}
              role="button"
              tabIndex={0}
              title="Click to copy"
              onClick={() => void handleCopyPrompt(prompt)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  void handleCopyPrompt(prompt);
                }
              }}
              sx={{
                backgroundColor: "background.default",
                borderRadius: 1,
                px: 2,
                py: 1.5,
                cursor: "pointer",
                transition: "background-color 140ms ease",
                "&:hover": {
                  backgroundColor: (t) => alpha(t.palette.primary.main, 0.06),
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontFamily: CALIBRATE_FONT_FAMILY_ACCENT }}
              >
                {prompt}
              </Typography>
            </Box>
          ))}
        </Box>
      </Card>

      <Card
        elevation={2}
        sx={{
          p: 2.5,
          backgroundColor: "calibrate.panelAccent",
          color: "#fff",
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontFamily: CALIBRATE_FONT_FAMILY_ACCENT, fontWeight: 700 }}
        >
          Need Help?
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
            color: "rgba(255,255,255,0.85)",
            mt: 1,
            mb: 2,
          }}
        >
          If the agent isn&rsquo;t returning what you expect or you have a
          suggestion, our team can help.
        </Typography>
        <DarkCtaButton
          fullWidth
          tone="ghostOnDark"
          onClick={() => setSupportOpen(true)}
          sx={{ fontWeight: 700 }}
        >
          Contact Support
        </DarkCtaButton>
      </Card>
    </>
  );

  if (userLoading) {
    return (
      <Layout title="AI Agent" content="AI Agent" noScroll={true}>
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <CircularProgress />
        </Container>
      </Layout>
    );
  }

  if (userError) {
    return (
      <Layout title="AI Agent" content="AI Agent">
        <Container
          component="main"
          maxWidth="sm"
          sx={{
            pt: { xs: 3, sm: 4 },
            pb: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Alert severity="error" sx={{ width: "100%" }}>
            <Typography variant="subtitle2" component="span" fontWeight={700}>
              Unable to verify your session
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {rtkQueryErrorMessage(userError)}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => void refetchUser()}>
            Try again
          </Button>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="AI Agent" content="AI Agent" noScroll={true}>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflowY: { xs: "auto", md: "hidden" },
          px: { xs: 2, sm: 3, md: 5 },
          py: { xs: 2, md: 3 },
          [VIEWPORT_2K_MIN_MEDIA]: { px: 6, py: 3.5 },
          [VIEWPORT_4K_MIN_MEDIA]: { px: 7, py: 4 },
        }}
      >
        <Typography
          variant="calibrateDisplay"
          component="h1"
          sx={{ fontSize: { xs: "1.75rem", md: "2.25rem", xl: "2.75rem" } }}
        >
          Your AI Pay History Tool
        </Typography>
        <Typography
          variant="calibrateSectionSubtitle"
          sx={{ mt: 0.5, fontSize: { xs: "0.95rem", md: "1rem", xl: "1.125rem" } }}
        >
          Your payroll data, on demand. Ask questions about your payroll history
          and get instant answers powered by AI.
        </Typography>

        {showBanner && (
          <Alert
            icon={false}
            sx={{
              mt: 2,
              borderLeft: "4px solid",
              borderLeftColor: "calibrate.panelAccent",
              backgroundColor: (t) =>
                alpha(t.palette.calibrate.panelAccent, 0.06),
              color: "text.primary",
              borderRadius: 1,
              "& .MuiAlert-action": { pt: 0 },
            }}
            action={
              <IconButton
                aria-label="Dismiss"
                size="small"
                onClick={handleDismissBanner}
              >
                <Close fontSize="small" />
              </IconButton>
            }
          >
            <Typography sx={{ fontWeight: 700 }}>
              First time here? Start with a question.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: CALIBRATE_FONT_FAMILY_ACCENT,
                color: "text.secondary",
              }}
            >
              Type naturally but be specific. Here&rsquo;s an example:
              &ldquo;Break down John Smith&rsquo;s net pay from February
              2024&rdquo; or use an example prompt from the panel to the right.
            </Typography>
          </Alert>
        )}

        <Box
          sx={{
            mt: 2,
            pb: { xs: 3, md: 0 },
            flex: { md: "1 1 0" },
            minHeight: 0,
            display: "grid",
            gap: "16px",
            alignItems: "stretch",
            gridTemplateColumns: {
              xs: "1fr",
              md: "minmax(0, 1fr) 340px",
            },
            [VIEWPORT_2K_MIN_MEDIA]: {
              gridTemplateColumns: "minmax(0, 1fr) 420px",
            },
            gridTemplateRows: { md: "minmax(0, 1fr)" },
          }}
        >
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              height: { xs: "70vh", md: "100%" },
              minHeight: { xs: 420, md: 0 },
            }}
          >
            {chatUrlBusy ? (
              iframeLoader
            ) : agentNotSetUp ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  p: 3,
                }}
              >
                <Alert severity="info" sx={{ width: "100%", maxWidth: 480 }}>
                  <Typography
                    variant="subtitle2"
                    component="span"
                    fontWeight={700}
                  >
                    Your AI Agent is not set up yet
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Your Calibrate team connects an agent to your account; it
                    appears here as soon as it is ready.
                  </Typography>
                </Alert>
                <Button variant="outlined" onClick={() => setSupportOpen(true)}>
                  Contact Support
                </Button>
              </Box>
            ) : chatUrlError || chatUrlMissing ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  p: 3,
                }}
              >
                <Alert severity="error" sx={{ width: "100%", maxWidth: 480 }}>
                  <Typography
                    variant="subtitle2"
                    component="span"
                    fontWeight={700}
                  >
                    AI Reporting unavailable
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {chatUrlMissing
                      ? "The assistant link was not returned. If this persists, contact your Calibrate administrator."
                      : rtkQueryErrorMessage(chatUrlError)}
                  </Typography>
                </Alert>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", justifyContent: "center" }}>
                  <Button
                    variant="contained"
                    onClick={() => void refetchChatUrl()}
                  >
                    Try again
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setSupportOpen(true)}
                  >
                    Contact Support
                  </Button>
                </Box>
              </Box>
            ) : iframeUrl ? (
              <iframe
                key={iframeUrl}
                title="AI Agent"
                style={{ width: "100%", height: "100%", border: 0, flex: 1 }}
                src={iframeUrl}
                allow="autoplay; fullscreen"
              />
            ) : (
              iframeLoader
            )}
          </Card>

          {isDesktop ? (
            <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
              <CustomScrollbars>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    pr: 1.5,
                    "& > *": { flexShrink: 0 },
                  }}
                >
                  {infoCards}
                </Box>
              </CustomScrollbars>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              {infoCards}
            </Box>
          )}
        </Box>
      </Box>

      <ContactSupportModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        onSubmitted={() => setSnackbar("Your request has been sent.")}
      />

      <Snackbar
        open={snackbar !== null}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
        message={snackbar ?? ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Layout>
  );
}
